import { expectRecord, expectString } from "./schema";
export const STUDENT_BILLING_CURRENCY = "JPY";
export const STUDENT_BILLING_PAYER_TYPES = ["platformUser", "externalContact"];
export const STUDENT_BILLING_PAYER_STATUSES = ["active", "inactive"];
export const STUDENT_BILLING_ASSIGNMENT_STATUSES = ["active", "ended"];
export const STUDENT_BILLING_CHARGE_TYPES = ["regularMonthly", "oneOff"];
export const STUDENT_BILLING_COLLECTION_ROUTES = ["stripe", "external"];
export const STUDENT_BILLING_AMOUNT_SOURCES = ["default", "manual"];
export const STUDENT_BILLING_UNIT_STATUSES = [
    "draft",
    "locked",
    "invoicePending",
    "paymentPending",
    "retryPending",
    "paid",
    "failed",
    "cancelled",
    "externalCollection"
];
function assertExactKeys(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0) {
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
    }
}
function parseEnum(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowed.join(", ")}`);
    }
    return parsed;
}
function parseNullableIdentifier(value, path) {
    if (value === null)
        return null;
    return parseStudentBillingIdentifier(value, path);
}
function parseNullableDate(value, path) {
    if (value === null)
        return null;
    return parseStudentBillingDate(value, path);
}
function parseExpectedVersion(value, path) {
    if (!Number.isSafeInteger(value) || value < 1) {
        throw new Error(`${path} must be a positive integer`);
    }
    return value;
}
export function parseStudentBillingIdentifier(value, path = "identifier") {
    const parsed = expectString(value, path).trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/u.test(parsed)) {
        throw new Error(`${path} must be a path-safe identifier`);
    }
    return parsed;
}
export function parseStudentBillingCurrency(value, path = "currency") {
    if (value !== STUDENT_BILLING_CURRENCY) {
        throw new Error(`${path} must be JPY`);
    }
    return STUDENT_BILLING_CURRENCY;
}
export function parseStudentBillingAmount(value, path = "amount") {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new Error(`${path} must be a non-negative integer JPY amount`);
    }
    return value;
}
export function parseStudentBillingDate(value, path = "date") {
    const parsed = expectString(value, path).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(parsed)) {
        throw new Error(`${path} must use YYYY-MM-DD`);
    }
    const instant = new Date(`${parsed}T00:00:00.000Z`);
    if (Number.isNaN(instant.getTime()) || instant.toISOString().slice(0, 10) !== parsed) {
        throw new Error(`${path} must be a valid calendar date`);
    }
    return parsed;
}
export function parseStudentBillingServiceMonth(value, path = "serviceMonth") {
    const parsed = expectString(value, path).trim();
    if (!/^\d{4}-(0[1-9]|1[0-2])$/u.test(parsed)) {
        throw new Error(`${path} must use YYYY-MM`);
    }
    return parsed;
}
export function parseStudentBillingAdjustmentReason(value, path = "reason") {
    const parsed = expectString(value, path).trim();
    if (parsed.length === 0 || parsed.length > 500) {
        throw new Error(`${path} must be between 1 and 500 characters`);
    }
    return parsed;
}
export function parseCreateStudentBillingPayerRequest(value, path = "createStudentBillingPayerRequest") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["payerId", "payerType", "platformUserId", "externalContactId", "billingAuthorityAcceptedAt"], path);
    const payerType = parseEnum(record.payerType, STUDENT_BILLING_PAYER_TYPES, `${path}.payerType`);
    const platformUserId = parseNullableIdentifier(record.platformUserId, `${path}.platformUserId`);
    const externalContactId = parseNullableIdentifier(record.externalContactId, `${path}.externalContactId`);
    if (payerType === "platformUser" && (!platformUserId || externalContactId)) {
        throw new Error(`${path} platformUser payer requires only platformUserId`);
    }
    if (payerType === "externalContact" && (!externalContactId || platformUserId)) {
        throw new Error(`${path} externalContact payer requires only externalContactId`);
    }
    return {
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        payerType,
        platformUserId,
        externalContactId,
        billingAuthorityAcceptedAt: record.billingAuthorityAcceptedAt === null
            ? null
            : new Date(expectString(record.billingAuthorityAcceptedAt, `${path}.billingAuthorityAcceptedAt`)).toISOString()
    };
}
export function parseAssignStudentBillingPayerRequest(value, path = "assignStudentBillingPayerRequest") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["assignmentId", "studentId", "payerId", "effectiveFrom", "effectiveTo"], path);
    const effectiveFrom = parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`);
    const effectiveTo = parseNullableDate(record.effectiveTo, `${path}.effectiveTo`);
    if (effectiveTo !== null && effectiveTo < effectiveFrom) {
        throw new Error(`${path}.effectiveTo must not precede effectiveFrom`);
    }
    return {
        assignmentId: parseStudentBillingIdentifier(record.assignmentId, `${path}.assignmentId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        effectiveFrom,
        effectiveTo
    };
}
export function parsePutRegularStudentBillingSettingRequest(value, path = "putRegularStudentBillingSettingRequest") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["studentId", "effectiveFrom", "effectiveTo", "amount", "currency", "expectedVersion"], path);
    const effectiveFrom = parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`);
    const effectiveTo = parseNullableDate(record.effectiveTo, `${path}.effectiveTo`);
    if (effectiveTo !== null && effectiveTo < effectiveFrom) {
        throw new Error(`${path}.effectiveTo must not precede effectiveFrom`);
    }
    return {
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        effectiveFrom,
        effectiveTo,
        amount: parseStudentBillingAmount(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        expectedVersion: record.expectedVersion === null
            ? null
            : parseExpectedVersion(record.expectedVersion, `${path}.expectedVersion`)
    };
}
export function parseCreateRegularStudentBillingUnitRequest(value, path = "createRegularStudentBillingUnitRequest") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["studentId", "payerId", "payerAssignmentId", "paidEnrollmentId", "serviceMonth", "defaultAmount", "currency", "collectionRoute"], path);
    return {
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        payerAssignmentId: parseStudentBillingIdentifier(record.payerAssignmentId, `${path}.payerAssignmentId`),
        paidEnrollmentId: parseStudentBillingIdentifier(record.paidEnrollmentId, `${path}.paidEnrollmentId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        defaultAmount: parseStudentBillingAmount(record.defaultAmount, `${path}.defaultAmount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        collectionRoute: parseEnum(record.collectionRoute, STUDENT_BILLING_COLLECTION_ROUTES, `${path}.collectionRoute`)
    };
}
export function parseCreateOneOffStudentBillingUnitRequest(value, path = "createOneOffStudentBillingUnitRequest") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["studentId", "payerId", "payerAssignmentId", "sourceType", "sourceId", "defaultAmount", "currency", "collectionRoute"], path);
    return {
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        payerAssignmentId: parseStudentBillingIdentifier(record.payerAssignmentId, `${path}.payerAssignmentId`),
        sourceType: parseStudentBillingIdentifier(record.sourceType, `${path}.sourceType`),
        sourceId: parseStudentBillingIdentifier(record.sourceId, `${path}.sourceId`),
        defaultAmount: parseStudentBillingAmount(record.defaultAmount, `${path}.defaultAmount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        collectionRoute: parseEnum(record.collectionRoute, STUDENT_BILLING_COLLECTION_ROUTES, `${path}.collectionRoute`)
    };
}
export function parseAdjustStudentBillingUnitAmountRequest(value, path = "adjustStudentBillingUnitAmountRequest") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["billingUnitId", "expectedVersion", "newAmount", "currency", "reason"], path);
    return {
        billingUnitId: parseStudentBillingIdentifier(record.billingUnitId, `${path}.billingUnitId`),
        expectedVersion: parseExpectedVersion(record.expectedVersion, `${path}.expectedVersion`),
        newAmount: parseStudentBillingAmount(record.newAmount, `${path}.newAmount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        reason: parseStudentBillingAdjustmentReason(record.reason, `${path}.reason`)
    };
}
export function studentBillingEffectivePeriodsOverlap(left, right) {
    const leftEnd = left.effectiveTo ?? "9999-12-31";
    const rightEnd = right.effectiveTo ?? "9999-12-31";
    return left.effectiveFrom <= rightEnd && right.effectiveFrom <= leftEnd;
}
export function studentBillingServiceMonthPeriod(serviceMonth) {
    const normalized = parseStudentBillingServiceMonth(serviceMonth);
    const year = Number(normalized.slice(0, 4));
    const month = Number(normalized.slice(5, 7));
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return {
        effectiveFrom: `${normalized}-01`,
        effectiveTo: `${normalized}-${String(lastDay).padStart(2, "0")}`
    };
}
export function isStudentBillingUnitAmountEditable(status) {
    return status === "draft";
}
